import { centerCount, selectedCount } from '@onuw/roles-data'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { PRESETS } from '../../ui/presets'
import { DECKS_BY_PLAYERS } from './deckList'
import styles from './guides.module.css'
import { Deck, R } from './parts'

/** What each ready-made deck of the role picker is for. */
const PRESET_NOTES: Record<string, string> = {
  classic: 'The deck to learn with: two wolves, three roles that look or move, one plain Villager.',
  daybreak:
    'The Alpha Wolf adds a fourth center card and a wolf who does not know it; the Witch and the Revealer keep the village in the game.',
  doppelganger:
    'The Doppelgänger among roles she plays at once, roles she joins, and roles she repeats after the original.',
  marks: 'Dusk before the night: five roles hand out marks, two more read them and steal them.',
  vampires: 'The Vampires, the Count and the Master against a village with a Priest and a Marksman.',
  aliens:
    'The Aliens with Groob, Zerb, the Synthetic Alien and the Body Snatcher, and the app calling their actions.',
  chaos: 'Four roles that move or copy cards, a Tanner and a Minion. Nobody is sure of anything by morning.',
  big: 'A ten-player table that wakes every kind of role: three wolves, two Aliens, an Oracle and a Curator.',
}

export function Decks() {
  const { t } = useTranslation()
  return (
    <>
      <h2>How many cards</h2>
      <p>
        A deck holds <strong>three cards more than there are players</strong>: everyone is dealt one, and
        three lie face down in the center. With the <R id="alphaWolf" /> in the deck there is a fourth center
        card, so the deck holds four more. The host builds the deck in the lobby, and the picker says how many
        cards are still missing. It also keeps the rules that come with some cards: <R id="mason">Masons</R>{' '}
        in pairs, a <R id="werewolf" /> beside the Alpha Wolf, at most two Werewolves, two{' '}
        <R id="alien">Aliens</R> and three <R id="villager">Villagers</R>.
      </p>

      <h2>A deck for every table size</h2>
      <p>
        These decks use the base game, with two <Link to="/guides/daybreak/">Daybreak</Link> roles at the
        largest tables. Each adds one idea to the one before, so a group can grow into them game by game.
      </p>
      <div className={styles.scroll}>
        <table>
          <thead>
            <tr>
              <th>Players</th>
              <th>Deck</th>
            </tr>
          </thead>
          <tbody>
            {DECKS_BY_PLAYERS.map((d) => (
              <tr key={d.players}>
                <td className={styles.num}>{d.players}</td>
                <td>
                  <Deck roles={d.roles} />
                  <br />
                  <span className="u-muted">{d.note}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2>The ready-made decks</h2>
      <p>
        The role picker has a button for each of these. A preset fills the deck for the number of players it
        was made for; with more or fewer players, add or remove cards until the picker says the deck is ready.
      </p>
      <div className={styles.scroll}>
        <table>
          <thead>
            <tr>
              <th>Deck</th>
              <th>Players</th>
              <th>Cards</th>
            </tr>
          </thead>
          <tbody>
            {PRESETS.map((p) => (
              <tr key={p.id}>
                <td>
                  <strong>{t(`ui:lobby.presets.${p.id}`)}</strong>
                </td>
                <td className={styles.num}>{selectedCount(p.roles) - centerCount(p.roles)}</td>
                <td>
                  <Deck roles={p.roles} />
                  {PRESET_NOTES[p.id] && (
                    <>
                      <br />
                      <span className="u-muted">{PRESET_NOTES[p.id]}</span>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2>Building your own</h2>
      <ul>
        <li>
          <strong>Two evil cards up to eight players, three from nine.</strong> Wolves that may both lie in
          the center are part of the game; a table where a third of the players are wolves is not much of one.
        </li>
        <li>
          <strong>Balance lookers and movers.</strong> The <R id="seer" />, the <R id="insomniac" /> and the{' '}
          <R id="revealer" /> give the village facts. The <R id="robber" />, the <R id="troublemaker" />, the{' '}
          <R id="witch" /> and the <R id="drunk" /> take them away again. Too many lookers and the wolves have
          nowhere to hide; too many movers and nobody can reason at all.
        </li>
        <li>
          <strong>One Villager is plenty.</strong> A player with nothing to do at night should be rare. Past
          the first games, swap Villagers for roles.
        </li>
        <li>
          <strong>Add one new role at a time.</strong> Everyone has to know every card in the deck to reason
          about it. The <Link to="/roles/">roles page</Link> has them all, each with its own page.
        </li>
        <li>
          <strong>Keep the lone players for bigger tables.</strong> A <R id="tanner" /> at three players
          decides too many games; from five up he is the best card in the box.
        </li>
        <li>
          <strong>Mix the packs with care.</strong> With Werewolves and{' '}
          <Link to="/guides/vampire/">Vampires</Link> or <Link to="/guides/alien/">Aliens</Link> among the
          players, the village has to hit every evil team in the same vote. That is hard, and best kept for
          groups who know all the roles.
        </li>
      </ul>
    </>
  )
}
